import { Box, Button, ButtonGroup, Card, Grid, TextField, Typography } from '@mui/material';
import 'quill/dist/quill.snow.css';
import { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../../app/AppStyles';
import { DisplayName, HandleImageUpload } from '../../../app/AppUtils';
import RichTextEditor from '../../subcomponents/RichTextEditor';
import { fetchNewsArticle, fetchNewsArticles, postNewsArticle, putNewsArticle, resetNewsArticleManagementStatus } from './newsManagementSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '40%' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function NewsEditCard({ handleClose, addNew, newsArticleId }) {
    const dispatch = useDispatch();
    const quillRef = useRef();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { newsArticle, newsArticlePost, newsArticlePut } = useSelector((state) => state.management.news);

    const [changed, setChanged] = useState(false);
    const [newNewsArticle, setNewNewsArticle] = useState({});

    const [errorImage, setErrorImage] = useState(null);
    const [imageName, setImageName] = useState(null);

    // Put status callback
    useEffect(() => {
        if (newsArticlePut.status === "success") {
            dispatch(resetNewsArticleManagementStatus());
            dispatch(fetchNewsArticle(newsArticleId));
            handleClose();
        } else if (newsArticlePut.status === "failed") {
            dispatch(resetNewsArticleManagementStatus());
        };
    }, [dispatch, handleClose, newsArticlePut.status]);

    // Post status callback
    useEffect(() => {
        if (newsArticlePost.status === "success") {
            dispatch(resetNewsArticleManagementStatus());
            dispatch(fetchNewsArticles());
            handleClose();
        } else if (newsArticlePost.status === "failed") {
            dispatch(resetNewsArticleManagementStatus());
        };
    }, [dispatch, handleClose, newsArticlePost.status]);

    // Selected period state update 
    useEffect(() => {
        if (!addNew && newsArticle.data) {
            setNewNewsArticle(newsArticle.data);
        }
    }, [newsArticle.data, addNew]);

    // Keep track of changed state 
    useEffect(() => {
        if (newNewsArticle !== newsArticle.data) {
            setChanged(true);
        } else {
            setChanged(false);
        };
    }, [newsArticle.data, newNewsArticle]);

    function handleImageChange(event) {
        setErrorImage(null);
        var imagePromise = HandleImageUpload(event);

        imagePromise.then(
            (value) => {
                setImageName(value?.name);
                setNewNewsArticle({
                    ...newNewsArticle,
                    imageUrl: value?.image
                })
            },
            (error) => {
                (error.message === "SIZE" && setErrorImage(resourceFinal[appLanguage].messages.ERROR_FILE_TOO_LARGE));
                (error.message === "TYPE" && setErrorImage(resourceFinal[appLanguage].messages.ERROR_FILE_NOT_IMAGE));
            });
    };

    // Update click function (check property character limits etc.)
    const updateClick = () => {
        let tempObject = { ...newNewsArticle }; // Needed to circumvent weird read only property error

        tempObject.createdBy = `AP/${user.number}`;
        tempObject.placedBy = `${DisplayName(user.firstName, user.middleName, user.lastName)}`;

        dispatch(putNewsArticle(tempObject));
    };

    // Add click function (check property character limits etc.)
    const addClick = () => {
        let tempObject = { ...newNewsArticle }; // Needed to circumvent weird read only property error

        tempObject.createdBy = `AP/${user.number}`;
        tempObject.placedBy = `${DisplayName(user.firstName, user.middleName, user.lastName)}`;

        dispatch(postNewsArticle(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.NEWS} {addNew ? resourceFinal[appLanguage].words.ADD.toLowerCase() : resourceFinal[appLanguage].words.EDIT.toLowerCase()}</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                            <TextField
                                fullWidth required
                                defaultValue={newsArticle.data?.title}
                                label={resourceFinal[appLanguage].words.TITLE}
                                onChange={(event) => setNewNewsArticle({
                                    ...newNewsArticle,
                                    title: event.target.value
                                })}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <Button
                                sx={{ width: 1, height: 1 }}
                                variant="outlined"
                                component="label"
                                color={errorImage ? "error" : "secondary"}
                            >
                                {imageName ? imageName : resourceFinal[appLanguage].words.IMAGE}
                                <input
                                    type="file"
                                    name="image"
                                    onChange={handleImageChange}
                                    hidden
                                />
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <RichTextEditor
                                ref={quillRef}
                                defaultValue={newsArticle.data?.message || ''}
                                onTextChange={(html) => setNewNewsArticle({
                                    ...newNewsArticle,
                                    message: html
                                })}
                            />
                        </Grid>
                        {(newsArticlePost.data === false || newsArticlePut.data === false) && <Grid size={{ xs: 12 }}>
                            <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                            <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{errorImage}</Typography>
                        </Grid>}
                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ButtonGroup sx={{ justifyContent: 'center' }}>
                                <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                                {!addNew && <Button disabled={!changed} variant="outlined" color="primary" onClick={updateClick}>{resourceFinal[appLanguage].words.SAVE}</Button>}
                                {addNew && <Button variant="outlined" color="primary" onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>}
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Scrollbars>
            </Card>
        </Box>
    );
}
